import React, { useState } from 'react';
import { Tabs, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useAuth } from '@/auth/hooks/useAuth';
import { useAdmins, useRoles, useWhitelistedIps } from '../hooks/useAdmins';
import { useAdminActions } from '../hooks/useAdminActions';
import { AdminsTable } from '../components/AdminsTable';
import { IpWhitelistTable } from '../components/IpWhitelistTable';
import { CreateAdminModal } from '../components/CreateAdminModal';
import { EditAdminModal } from '../components/EditAdminModal';
import { MfaResetModal } from '../components/MfaResetModal';
import { AddIpModal } from '../components/AddIpModal';
import type { Admin } from '../types/admin.types';

const { TabPane } = Tabs;

export const AdminsScreen: React.FC = () => {
    const { isSuperAdmin } = useAuth();
    const [activeTab, setActiveTab] = useState('1');
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
    const [mfaResetModalVisible, setMfaResetModalVisible] = useState(false);
    const [resetingAdmin, setResetingAdmin] = useState<Admin | null>(null);
    const [ipModalVisible, setIpModalVisible] = useState(false);

    // Queries
    const { data: admins, isLoading: loadingAdmins } = useAdmins();
    const { data: ips, isLoading: loadingIps } = useWhitelistedIps();
    const { data: roles, isLoading: loadingRoles } = useRoles();

    // Mutations
    const {
        createAdmin,
        updateAdmin,
        deleteAdmin,
        disableAdmin,
        enableAdmin,
        resetMfa,
        addWhitelistedIp,
        removeWhitelistedIp
    } = useAdminActions();

    // Handlers
    const handleCreateAdmin = (values: any) => {
        createAdmin.mutate(values, {
            onSuccess: () => setCreateModalVisible(false)
        });
    };

    const handleEditAdmin = (admin: Admin) => {
        setEditingAdmin(admin);
        setEditModalVisible(true);
    };

    const handleUpdateAdmin = (id: string, values: any) => {
        updateAdmin.mutate({ id, values }, {
            onSuccess: () => {
                setEditModalVisible(false);
                setEditingAdmin(null);
            }
        });
    };

    const handleResetMfa = (admin: Admin) => {
        setResetingAdmin(admin);
        setMfaResetModalVisible(true);
    };

    const handleMfaResetSubmit = (id: string, reason: string) => {
        resetMfa.mutate({ id, reason }, {
            onSuccess: () => {
                setMfaResetModalVisible(false);
                setResetingAdmin(null);
            }
        });
    };

    const handleAddIp = (values: any) => {
        addWhitelistedIp.mutate(values, {
            onSuccess: () => setIpModalVisible(false)
        });
    };

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1 style={{ fontSize: 24, margin: 0 }}>Admin Management</h1>
                {isSuperAdmin && activeTab === '1' && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
                        New Admin
                    </Button>
                )}
            </div>

            <Tabs defaultActiveKey="1" onChange={setActiveTab}>
                <TabPane tab="Administrators" key="1">
                    <AdminsTable
                        admins={Array.isArray(admins?.data) ? admins.data : (Array.isArray(admins) ? admins : [])}
                        loading={loadingAdmins}
                        isSuperAdmin={isSuperAdmin}
                        onEdit={handleEditAdmin}
                        onDisable={(id) => disableAdmin.mutate(id)}
                        onEnable={(id) => enableAdmin.mutate(id)}
                        onDelete={(id) => deleteAdmin.mutate(id)}
                        onResetMfa={handleResetMfa}
                    />
                </TabPane>
                <TabPane tab="IP Whitelist" key="2">
                    <div style={{ marginBottom: 16, textAlign: 'right' }}>
                        {isSuperAdmin && (
                            <Button icon={<PlusOutlined />} onClick={() => setIpModalVisible(true)}>
                                Add IP
                            </Button>
                        )}
                    </div>
                    <IpWhitelistTable
                        ips={Array.isArray(ips?.data) ? ips.data : (Array.isArray(ips) ? ips : [])}
                        loading={loadingIps}
                        isSuperAdmin={isSuperAdmin}
                        onDelete={(id) => removeWhitelistedIp.mutate(id)}
                    />
                </TabPane>
            </Tabs>

            <CreateAdminModal
                open={createModalVisible}
                loading={createAdmin.isPending}
                roles={Array.isArray(roles?.data) ? roles.data : (Array.isArray(roles) ? roles : [])}
                loadingRoles={loadingRoles}
                onCancel={() => setCreateModalVisible(false)}
                onSubmit={handleCreateAdmin}
            />

            <EditAdminModal
                open={editModalVisible}
                loading={updateAdmin.isPending}
                admin={editingAdmin}
                roles={Array.isArray(roles?.data) ? roles.data : (Array.isArray(roles) ? roles : [])}
                loadingRoles={loadingRoles}
                onCancel={() => {
                    setEditModalVisible(false);
                    setEditingAdmin(null);
                }}
                onSubmit={handleUpdateAdmin}
            />

            <MfaResetModal
                open={mfaResetModalVisible}
                loading={resetMfa.isPending}
                admin={resetingAdmin}
                onCancel={() => {
                    setMfaResetModalVisible(false);
                    setResetingAdmin(null);
                }}
                onSubmit={handleMfaResetSubmit}
            />

            <AddIpModal
                open={ipModalVisible}
                loading={addWhitelistedIp.isPending}
                onCancel={() => setIpModalVisible(false)}
                onSubmit={handleAddIp}
            />
        </div>
    );
};
